import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, DollarSign, Briefcase, ExternalLink, Lock, CheckCircle, AlertCircle } from 'lucide-react';

const InterviewSection = ({ profile, onRefresh }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCalendlyModal, setShowCalendlyModal] = useState(false);

  const canAccessInterview = profile?.paymentStatus === 'paid' && profile?.sectionsCompleted?.documents;
  const selectedRoles = profile?.selectedRolesWithDetails || [];

  const openCalendly = (role) => {
    if (role.roleDetails?.calendlyLink) {
      window.open(role.roleDetails.calendlyLink, '_blank', 'noopener,noreferrer');
    } else {
      alert('Interview booking link is not available for this role yet. Please contact support.');
    }
  };

  if (!canAccessInterview) {
    return (
      <div className="p-6 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border-2 border-gray-200 p-8 text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
            <Lock className="text-gray-500" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Schedule Interview</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Complete your payment and upload all required documents to unlock interview scheduling.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <div className="flex items-center gap-2 text-sm">
              {profile?.paymentStatus === 'paid' ? (
                <CheckCircle className="text-green-600" size={18} />
              ) : (
                <AlertCircle className="text-orange-500" size={18} />
              )}
              <span className={profile?.paymentStatus === 'paid' ? 'text-green-700' : 'text-orange-700'}>
                Payment {profile?.paymentStatus === 'paid' ? 'Complete' : 'Required'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {profile?.sectionsCompleted?.documents ? (
                <CheckCircle className="text-green-600" size={18} />
              ) : (
                <AlertCircle className="text-orange-500" size={18} />
              )}
              <span className={profile?.sectionsCompleted?.documents ? 'text-green-700' : 'text-orange-700'}>
                Documents {profile?.sectionsCompleted?.documents ? 'Complete' : 'Required'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (selectedRoles.length === 0) {
    return (
      <div className="p-6 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-2xl font-bold text-[#202356] mb-2">Schedule Interview</h1>
          <p className="text-gray-600 mb-6">
            Book interviews with employers for your selected roles
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <AlertCircle className="text-blue-600 mx-auto mb-3" size={40} />
            <h3 className="text-lg font-semibold text-blue-900 mb-2">No Roles Selected</h3>
            <p className="text-blue-800 mb-4">
              You haven't selected any roles yet. Please go to the Role Preferences section and select up to 2 roles you're interested in.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#202356] mb-2">Schedule Interview</h1>
          <p className="text-gray-600">
            Book interviews with employers for your selected roles
          </p>
        </div>

        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-green-900 mb-1">You're Ready to Schedule!</h3>
              <p className="text-sm text-green-800">
                Click the "Book Interview" button below to schedule a time that works for you. You'll be redirected to our scheduling platform.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {selectedRoles.map((role, index) => {
            const roleDetails = role.roleDetails;
            if (!roleDetails) return null;

            return (
              <motion.div
                key={role.roleId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:border-[#202356] hover:shadow-lg transition-all"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#202356] mb-2">
                          {roleDetails.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <MapPin size={16} className="text-gray-400" />
                            <span>{roleDetails.city ? `${roleDetails.city}, ` : ''}{roleDetails.country}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Briefcase size={16} className="text-gray-400" />
                            <span>{roleDetails.contractType}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#202356] text-white rounded-lg">
                        <DollarSign size={18} />
                        <span className="font-semibold">{roleDetails.salaryRange}</span>
                      </div>
                    </div>

                    {roleDetails.description && (
                      <p className="text-gray-700 mb-4 line-clamp-2">
                        {roleDetails.description}
                      </p>
                    )}

                    {roleDetails.benefits && roleDetails.benefits.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Benefits:</h4>
                        <div className="flex flex-wrap gap-2">
                          {roleDetails.benefits.slice(0, 3).map((benefit, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                            >
                              {benefit}
                            </span>
                          ))}
                          {roleDetails.benefits.length > 3 && (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{roleDetails.benefits.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="lg:w-64 flex-shrink-0">
                    <button
                      onClick={() => openCalendly(role)}
                      disabled={!roleDetails.calendlyLink}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold transition-all ${
                        roleDetails.calendlyLink
                          ? 'bg-[#202356] text-white hover:bg-[#2542D4] hover:shadow-lg'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <Calendar size={20} />
                      <span>Book Interview</span>
                      {roleDetails.calendlyLink && <ExternalLink size={16} />}
                    </button>

                    {!roleDetails.calendlyLink && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        Booking link coming soon
                      </p>
                    )}

                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 text-center">
                        You'll be redirected to our scheduling platform to choose a convenient time
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Interview Tips</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Prepare your questions about the role and company in advance</li>
            <li>• Have your CV and relevant documents ready to discuss</li>
            <li>• Ensure you have a stable internet connection for video interviews</li>
            <li>• Join the meeting 5 minutes early to test your audio and video</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
};

export default InterviewSection;
